import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  loginSchema,
  loginType,
} from "../../../interface-and-types/auth.interface";
import apiClient from "../../../api-services/ApiClient";
import PasswordToggle from "../../../app-components/toggle-password/PasswordToggle";
import { SITELINKS } from "../../../app-routes/links";
import { useGlobalState } from "../../../context/GlobalStateContext";
import AuthLayout from "../common/AuthLayout";

const OperatorLogin = () => {
  const navigate = useNavigate();
  const { dispatch } = useGlobalState();

  const [loginData, setLoginData] = useState<loginType>({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof loginType, string>>
  >({});

  const [serverError, setServerError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});

    const result = loginSchema.safeParse(loginData);

    if (result.success) {
      setLoading(true);
      try {
        const res = await apiClient.post(
          "Authentication/External/Login",
          result.data
        );

        const data = res.data.data;

        dispatch({ type: "SET_TEPNGUSER", payload: data });
        // setAuthorizationToken(data?.accessToken);

        const loginRedirectedFromUrl = localStorage.getItem(
          "loginRedirectedFromUrl"
        );
        localStorage.removeItem("loginRedirectedFromUrl");

        let userView = SITELINKS.DASHBOARD_VO;

        navigate(loginRedirectedFromUrl ?? userView);
      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<Record<keyof loginType, string>> = {};

      result.error.errors.forEach((err) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof loginType;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);
    }
  };

  return (
    <AuthLayout title="Welcome to MVIP">
      <p className="flex-col text-xs pt-1 text-slate-700 px-12 text-center">
        Sign in to your Vessel Operator account to continue
      </p>

      <form
        className="p-12 pt-3 md:px-48 md:py-12 lg:px-56 xl:px-8 xl:py-12 xl:pt-4"
        onSubmit={handleEmailLogin}
        autoComplete="off"
        autoCorrect="off"
      >
        <div className="md:bg-white md:p-12 xl:py-0 md:shadow-card-box md:rounded-md xl:shadow-none">
          <p className="text-red-600 text-xs mb-4 text-center">{serverError}</p>

          {/* email input */}
          <div className="flex flex-col text-xs mb-4 md:mb-5 xl:mb-4">
            <label htmlFor="userID" className="mb-0.5 text-slate-700">
              Email Address
            </label>

            <input
              name="email"
              onChange={handleInputChange}
              className="w-full border border-solid border-slate-300 text-slate-700 p-3 rounded focus:outline-1 focus:outline-none focus:border-blue-500 font-normal hover:shadow-sm bg-slate-50"
              placeholder="yourmail@example.com"
              autoComplete="off"
            />

            <span className="text-red-600 text-[10px]">{errors.email}</span>
          </div>

          {/* password input */}
          <div className="flex flex-col text-xs mb-4 md:mb-5 xl:mb-4 relative">
            <label htmlFor="Password" className="mb-0.5 text-slate-600">
              Password
            </label>

            <input
              name="password"
              id="password_input"
              type="password"
              onChange={handleInputChange}
              className="w-full bg-slate-50 border border-solid border-slate-300 text-slate-700 p-3 rounded focus:outline-1 focus:outline-none focus:border-blue-500 font-normal hover:shadow-sm"
              placeholder="Enter your password"
              autoComplete="new-password"
            />

            <div className="absolute top-7 right-3 mt-0.5">
              <PasswordToggle targetId="password_input" />
            </div>

            <span className="text-red-600 text-[10px]">{errors.password}</span>
          </div>

          {/* remember me */}
          <div className="flex justify-between items-center mb-4 md:mb-5 xl:mb-4">
            <div className="flex text-xs gap-2 items-center text-slate-700">
              <input type="checkbox" id="remember_me" className="h-4 w-4" />
              <label htmlFor="remember_me" className="mt-[1px]">
                Remember Me
              </label>
            </div>

            <Link
              className="text-xs text-slate-700"
              to={SITELINKS.RESET_PASSWORD}
            >
              Forgot Password ?
            </Link>
          </div>

          <div className="flex justify-center items-center mb-4 md:mb-5 xl:mb-4">
            <button
              type="submit"
              disabled={loading}
              className="disabled:bg-gray-200 disabled:text-gray-600 mt-5 bg-red-600 text-white px-2 py-3 shadow-sm hover:shadow-xl w-4/5 xl:w-full rounded text-sm font-medium"
            >
              {loading ? "Loading..." : "Sign In"}
            </button>
          </div>

          <div className="text-left text-slate-600 mt-8 text-xs text-center">
            <span>Are you a Marine Superintendent? </span>
            <Link
              className="text-blue-700 font-medium whitespace-nowrap"
              to={SITELINKS.SIGNIN}
            >
              Sign in here
            </Link>
          </div>
        </div>
      </form>
    </AuthLayout>
  );
};

export default OperatorLogin;
